import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  SimpleGrid,
  Stack,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useRef, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import {
  getMyOrganisation,
  overrideOrganisationBoxes,
  saveOrganisation,
} from '../../../stores/slices/organisations/organisationsActions';
import DeleteAlert from '../../Alerts/DeleteAlert';
import CustomEditor from './CustomEditor';

const MyOrg = () => {
  const { myOrganisation } = useSelector(state => state.organisations);
  const schema = yup
    .object({
      name: yup.string().required('Organisation name is required'),
    })
    .required();

  const dispatch = useDispatch();
  const toast = useToast();
  const [group1Tip, setGroup1Tip] = useState('');
  const [group2Tip, setGroup2Tip] = useState('');
  const [group3Tip, setGroup3Tip] = useState('');
  const [group4Tip, setGroup4Tip] = useState('');
  const [isOverriding, setIsOverriding] = useState(false);
  const cancelRef = useRef(null);
  const {
    isOpen: isOpenAlert,
    onOpen: onOpenAlert,
    onClose: onCloseAlert,
  } = useDisclosure();
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
    getValues,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    dispatch(getMyOrganisation());
  }, []);

  useEffect(() => {
    reset({
      name: myOrganisation.name,
      group1Default: myOrganisation.group_one_default || '',
      group2Default: myOrganisation.group_two_default || '',
      group3Default: myOrganisation.group_three_default || '',
      group4Default: myOrganisation.group_four_default || '',
    });
  }, [myOrganisation]);

  const onSave = async data => {
    let orgData = {
      id: myOrganisation.id,
      name: data.name,
      group_one_default: data.group1Default,
      group_two_default: data.group2Default,
      group_three_default: data.group3Default,
      group_four_default: data.group4Default,
      group_one_tip: group1Tip,
      group_two_tip: group2Tip,
      group_three_tip: group3Tip,
      group_four_tip: group4Tip,
    };

    const res = await dispatch(
      saveOrganisation({
        data: orgData,
        isUpdate: {
          state: true,
          organisation: orgData,
        },
        myOrg: true,
      })
    );
    if (res.type === 'organisations/saveOrganisation/rejected') {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    } else {
      toast({
        description: 'Organisation has been updated',
        colorScheme: 'primary',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
        variant: 'primary',
      });
    }
  };

  const onOverride = async () => {
    setIsOverriding(true);
    const data = getValues();
    let orgData = {
      group_one_default: data.group1Default,
      group_two_default: data.group2Default,
      group_three_default: data.group3Default,
      group_four_default: data.group4Default,
      group_one_tip: group1Tip,
      group_two_tip: group2Tip,
      group_three_tip: group3Tip,
      group_four_tip: group4Tip,
    };

    const res = await dispatch(
      overrideOrganisationBoxes({
        data: orgData,
      })
    );
    setIsOverriding(false);
    onCloseAlert();
    if (res.type === 'organisations/overrideOrganisationBoxes/rejected') {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    } else {
      toast({
        description: 'Organisation has been updated',
        colorScheme: 'primary',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
        variant: 'primary',
      });
    }
  };
  return (
    <Box w={'full'} overflowX={'hidden'} overflowY={'auto'} mt={'2'}>
      <Scrollbars>
        <form onSubmit={handleSubmit(onSave)}>
          <VStack spacing={'2'} pr={'4'}>
            <Stack
              w={'full'}
              justifyContent={'space-between'}
              direction={{ base: 'column-reverse', md: 'row' }}
            >
              <FormControl
                id="name"
                isInvalid={errors.name}
                maxW={{ base: '100%', md: '40%' }}
              >
                <FormLabel>Organisation Name</FormLabel>
                <Input
                  id="name"
                  {...register('name')}
                  defaultValue={myOrganisation ? myOrganisation.name : ''}
                />
                <FormErrorMessage>
                  {errors.name && errors.name.message}
                </FormErrorMessage>
              </FormControl>
              <HStack align={'start'}>
                <Button
                  type="button"
                  bg={'red.400'}
                  _hover={{
                    color: 'red.400',
                    bg: 'transparent',
                  }}
                  onClick={onOpenAlert}
                >
                  Override 4Boxes
                </Button>
                <Button type="submit" isLoading={isSubmitting}>
                  Save
                </Button>
              </HStack>
            </Stack>
            <SimpleGrid
              columns={{ base: 1, lg: 2 }}
              spacingX={2}
              spacingY={{ base: 2, lg: 2 }}
              w={'full'}
            >
              <VStack>
                <FormControl
                  id="group1Default"
                  isInvalid={errors.group1Default}
                >
                  <FormLabel>Item group one & tool tip</FormLabel>
                  <Input
                    id="group1Default"
                    {...register('group1Default')}
                    defaultValue={
                      myOrganisation ? myOrganisation.group_one_default : ''
                    }
                  />
                  <FormErrorMessage>
                    {errors.group1Default && errors.group1Default.message}
                  </FormErrorMessage>
                </FormControl>
                <CustomEditor
                  onChange={setGroup1Tip}
                  height={'300px'}
                  val={
                    myOrganisation && myOrganisation.group_one_tip
                      ? myOrganisation.group_one_tip
                      : ''
                  }
                />
              </VStack>
              <VStack>
                <FormControl
                  id="group2Default"
                  isInvalid={errors.group2Default}
                >
                  <FormLabel>Item group two & tool tip</FormLabel>
                  <Input
                    id="group2Default"
                    {...register('group2Default')}
                    defaultValue={
                      myOrganisation ? myOrganisation.group_two_default : ''
                    }
                  />
                  <FormErrorMessage>
                    {errors.group2Default && errors.group2Default.message}
                  </FormErrorMessage>
                </FormControl>
                <CustomEditor
                  onChange={setGroup2Tip}
                  height={'300px'}
                  val={
                    myOrganisation && myOrganisation.group_two_tip
                      ? myOrganisation.group_two_tip
                      : ''
                  }
                />
              </VStack>
              <VStack>
                <FormControl
                  id="group3Default"
                  isInvalid={errors.group3Default}
                >
                  <FormLabel>Item group three & tool tip</FormLabel>
                  <Input
                    id="group3Default"
                    {...register('group3Default')}
                    defaultValue={
                      myOrganisation ? myOrganisation.group_three_default : ''
                    }
                  />
                  <FormErrorMessage>
                    {errors.group3Default && errors.group3Default.message}
                  </FormErrorMessage>
                </FormControl>
                <CustomEditor
                  onChange={setGroup3Tip}
                  height={'300px'}
                  val={
                    myOrganisation && myOrganisation.group_three_tip
                      ? myOrganisation.group_three_tip
                      : ''
                  }
                />
              </VStack>
              <VStack>
                <FormControl
                  id="group4Default"
                  isInvalid={errors.group4Default}
                >
                  <FormLabel>Item group four & tool tip</FormLabel>
                  <Input
                    id="group4Default"
                    {...register('group4Default')}
                    defaultValue={
                      myOrganisation ? myOrganisation.group_four_default : ''
                    }
                  />
                  <FormErrorMessage>
                    {errors.group4Default && errors.group4Default.message}
                  </FormErrorMessage>
                </FormControl>
                <CustomEditor
                  onChange={setGroup4Tip}
                  height={'300px'}
                  val={
                    myOrganisation && myOrganisation.group_four_tip
                      ? myOrganisation.group_four_tip
                      : ''
                  }
                />
              </VStack>
            </SimpleGrid>
          </VStack>
        </form>
      </Scrollbars>
      <DeleteAlert
        isOpen={isOpenAlert}
        cancelRef={cancelRef}
        onClose={onCloseAlert}
        isDeleting={isOverriding}
        header={`Override 4Boxes?`}
        body={
          'Are you sure? This will override the category and tooltip on all your employees.'
        }
        onDeleteItem={onOverride}
        deleteText={'Override'}
      />
    </Box>
  );
};

export default MyOrg;
